// internationalization
import type { TFunction } from 'i18next';

// api
import type { PmMeterType } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisPMM';

// shared components
import type { DanishAddressFieldValue } from '@/components/ui/form';

// components
// feature types
import type {
  AnalysisType,
  BuildingWithMatrikelKey,
  PortfolioMeter,
} from '@/features/energyxplorer/types';

// libraries
import { z } from 'zod';

export const SITE_MODE_OPTIONS = ['existing', 'new'] as const;
export type SiteMode = (typeof SITE_MODE_OPTIONS)[number];

export const PM_METER_TYPES: PmMeterType[] = [
  'solar_panels',
  'heat_pump',
  'power_meter',
];

/** A PMM unit (UnitTypeId=30) not yet added to portfolio analysis. */
export interface PmmMeterOption {
  source: 'pmm';
  unitId: number;
  name: string;
}

/** A portfolio meter not yet linked to a matrikel. */
export interface PortfolioMeterOption {
  source: 'portfolio';
  meter: PortfolioMeter;
  analysisType: AnalysisType;
  name: string;
}

export type UnifiedMeterOption = PmmMeterOption | PortfolioMeterOption;

export function createSiteFormSchema(t: TFunction) {
  return z
    .object({
      siteMode: z.enum(SITE_MODE_OPTIONS),
      existingAddress: z.custom<BuildingWithMatrikelKey>().nullable(),
      newAddress: z.custom<DanishAddressFieldValue>().nullable(),
      meter: z.custom<UnifiedMeterOption>().nullable(),
      meterType: z.string(),
    })
    .superRefine((data, ctx) => {
      if (data.siteMode === 'existing' && !data.existingAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('input.required', { ns: 'validation' }),
          path: ['existingAddress'],
        });
      }
      if (
        data.siteMode === 'new' &&
        (!data.newAddress?.displayName?.trim() ||
          !Number.isFinite(data.newAddress?.latitude) ||
          !Number.isFinite(data.newAddress?.longitude))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('input.required', { ns: 'validation' }),
          path: ['newAddress'],
        });
      }
      if (!data.meter) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('input.required', { ns: 'validation' }),
          path: ['meter'],
        });
      }
      if (data.meter?.source === 'pmm' && !data.meterType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('input.required', { ns: 'validation' }),
          path: ['meterType'],
        });
      }
    });
}

export type CreateSiteFormValues = z.infer<
  ReturnType<typeof createSiteFormSchema>
>;
